// ui/itemDatabaseModal.js
// Item Database modal - wiki-style reference for all items

import { getAllItems, getItemIcon, getItemName, ITEM_CATEGORIES } from '../data/items/index.js';
import { ZONE_LOOT, MOB_LOOT, COMBAT_ZONES, MOBS } from '../skills/combat/combatData.js';

// Skill data imports for building item sources
import { ROCKS } from '../skills/mining/miningData.js';
import { BARS, ARROW_HEADS } from '../skills/smithing/smithingData.js';
import { TREES } from '../skills/woodcutting/woodcuttingData.js';
import { LOGS as PYRE_LOGS } from '../skills/pyre/pyreData.js';
import { FISH } from '../skills/fishing/fishingData.js';
import { FOOD, EMPOWERED_RECIPES } from '../skills/cooking/cookingData.js';
import { GRASS_PATCH, CROPS, HERBS } from '../skills/farming/farmingData.js';
import { HERB_CLEANING, POTION_RECIPES } from '../skills/alchemy/alchemyData.js';
import { SHAFT_RECIPES, ARROW_RECIPES, BOW_RECIPES, STAFF_RECIPES } from '../skills/woodworking/woodworkingData.js';
import { JEWELRY, ALL_ARMOR, ALL_MAGIC_ARMOR } from '../skills/crafting/craftingData.js';
import { ESSENCE_NODES, GLYPH_RECIPES, GLYPH_CORE_RECIPES, FOCUS_RECIPES } from '../skills/glyphbinding/glyphbindingData.js';
import { TIER_DROPS } from '../skills/gatherer/gathererData.js';
import { getAllShopItems } from '../data/shop/shopData.js';
import { PET_CONTRACTS } from '../skills/husbandry/husbandryData.js';
import { GUILD_DOMAINS, DOMAIN_TIER_UPGRADES } from '../skills/pilfering/pilferingData.js';
import { QUESTS } from '../data/quests/questData.js';

let modalElement = null;
let searchFilter = '';
let selectedCategory = 'all';
let searchDebounceTimer = null;
let showSpoilers = false;
let revealedSpoilers = new Set();

/**
 * Category display configuration
 */
const CATEGORY_CONFIG = {
    all: { name: 'All Items', icon: '📦' },
    ore: { name: 'Ores', icon: '�ite' },
    bar: { name: 'Bars', icon: '🔩' },
    log: { name: 'Logs', icon: '🪵' },
    fish_raw: { name: 'Raw Fish', icon: '🐟' },
    fish_cooked: { name: 'Cooked Fish', icon: '🍣' },
    crop: { name: 'Crops', icon: '🌾' },
    seed: { name: 'Seeds', icon: '🌱' },
    herb_seed: { name: 'Herb Seeds', icon: '🌿' },
    herb_grimy: { name: 'Grimy Herbs', icon: '🥬' },
    herb_clean: { name: 'Clean Herbs', icon: '🌿' },
    potion: { name: 'Potions', icon: '🧪' },
    gem: { name: 'Gems', icon: '💎' },
    hide: { name: 'Hides', icon: '🦎' },
    cloth: { name: 'Cloth', icon: '🧵' },
    bone: { name: 'Bones', icon: '🦴' },
    armor: { name: 'Armor', icon: '🛡️' },
    weapon: { name: 'Weapons', icon: '⚔️' },
    bow: { name: 'Bows', icon: '🏹' },
    arrow: { name: 'Arrows', icon: '➳' },
    arrowhead: { name: 'Arrowheads', icon: '🔺' },
    arrow_shaft: { name: 'Arrow Shafts', icon: '|' },
    band: { name: 'Rings', icon: '💍' },
    chain: { name: 'Necklaces', icon: '📿' },
    sigil: { name: 'Sigils', icon: '🔮' },
    ichor: { name: 'Ichor', icon: '✨' },
    glyph: { name: 'Glyphs', icon: '🔷' },
    glyph_core: { name: 'Glyph Cores', icon: '💠' },
    pet_food: { name: 'Pet Food', icon: '🥩' },
    pet_material: { name: 'Pet Materials', icon: '🐾' },
    misc: { name: 'Miscellaneous', icon: '📋' }
};

/**
 * Helper to add a source to an item
 */
function addSource(sources, itemId, sourceName) {
    if (sources.has(itemId)) {
        const data = sources.get(itemId);
        if (!data.sources.includes(sourceName)) {
            data.sources.push(sourceName);
        }
    }
}

/**
 * Helper to add "usedIn" reference for an input item
 */
function addUsedIn(sources, itemId, usedInName) {
    if (sources.has(itemId)) {
        const data = sources.get(itemId);
        if (!data.usedIn.includes(usedInName)) {
            data.usedIn.push(usedInName);
        }
    }
}

/**
 * Build item source data - where items come from
 * Dynamically pulls from all skill data files
 * Returns a map of itemId -> { sources: [], usedIn: [], isSpoiler: bool }
 */
function buildItemSources() {
    const sources = new Map();

    // Initialize all items
    for (const item of getAllItems()) {
        sources.set(item.id, {
            sources: [],
            usedIn: [],
            isSpoiler: false
        });
    }

    // ============= GATHERING SOURCES (not spoilers) =============

    // Mining - rocks drop ores and gems
    for (const rock of Object.values(ROCKS)) {
        for (const drop of rock.drops || []) {
            addSource(sources, drop.itemId, 'Mining');
        }
    }

    // Woodcutting - trees drop logs
    for (const tree of Object.values(TREES)) {
        for (const drop of tree.drops || []) {
            addSource(sources, drop.itemId, 'Woodcutting');
        }
    }

    // Fishing - fish spots drop raw fish
    for (const fish of Object.values(FISH)) {
        for (const drop of fish.drops || []) {
            addSource(sources, drop.itemId, 'Fishing');
        }
    }

    // Farming - grass patch drops seeds
    if (GRASS_PATCH?.drops) {
        for (const drop of GRASS_PATCH.drops) {
            addSource(sources, drop.itemId, 'Farming (Raking)');
        }
    }

    // Farming - crops drop produce
    for (const crop of Object.values(CROPS || {})) {
        for (const drop of crop.drops || []) {
            addSource(sources, drop.itemId, 'Farming');
        }
        // Seeds used in farming
        if (crop.seedId) {
            addUsedIn(sources, crop.seedId, 'Farming');
        }
    }

    // Farming - herbs drop grimy herbs
    for (const herb of Object.values(HERBS || {})) {
        for (const drop of herb.drops || []) {
            addSource(sources, drop.itemId, 'Farming');
        }
        if (herb.seedId) {
            addUsedIn(sources, herb.seedId, 'Farming');
        }
    }

    // Glyphbinding - essence nodes drop ichor
    for (const node of Object.values(ESSENCE_NODES || {})) {
        for (const drop of node.drops || []) {
            addSource(sources, drop.itemId, 'Glyphbinding');
        }
    }

    // Gatherer - tier drops for pet food and rare materials
    for (const tierData of Object.values(TIER_DROPS || {})) {
        if (tierData.food) addSource(sources, tierData.food, 'Gatherer');
        if (tierData.rare) addSource(sources, tierData.rare, 'Gatherer');
        for (const bonus of tierData.bonusFood || []) {
            addSource(sources, bonus.itemId, 'Gatherer');
        }
        for (const bonus of tierData.bonusMaterials?.items || []) {
            addSource(sources, bonus.itemId, 'Gatherer');
        }
    }

    // ============= PROCESSING SOURCES (not spoilers) =============

    // Smithing - bars from ores
    for (const bar of Object.values(BARS || {})) {
        for (const drop of bar.drops || []) {
            addSource(sources, drop.itemId, 'Smithing');
        }
        if (bar.inputItem) addUsedIn(sources, bar.inputItem, 'Smithing');
        if (bar.secondaryItem) addUsedIn(sources, bar.secondaryItem, 'Smithing');
    }

    // Smithing - arrowheads from bars
    for (const ah of Object.values(ARROW_HEADS || {})) {
        for (const drop of ah.drops || []) {
            addSource(sources, drop.itemId, 'Smithing');
        }
        if (ah.inputItem) addUsedIn(sources, ah.inputItem, 'Smithing');
    }

    // Pyre - burns logs to produce ashes
    for (const log of Object.values(PYRE_LOGS || {})) {
        for (const drop of log.drops || []) {
            addSource(sources, drop.itemId, 'Pyre');
        }
        if (log.inputItem) addUsedIn(sources, log.inputItem, 'Pyre');
    }

    // Cooking - cooks raw fish
    for (const food of Object.values(FOOD || {})) {
        for (const drop of food.drops || []) {
            addSource(sources, drop.itemId, 'Cooking');
        }
        if (food.inputItem) addUsedIn(sources, food.inputItem, 'Cooking');
    }

    // Cooking - empowered recipes (cooked fish + crop)
    for (const recipe of Object.values(EMPOWERED_RECIPES || {})) {
        if (recipe.output?.itemId) {
            addSource(sources, recipe.output.itemId, 'Cooking (Empowered)');
        }
        for (const input of recipe.inputs || []) {
            addUsedIn(sources, input.itemId, 'Cooking');
        }
    }

    // Alchemy - herb cleaning
    for (const recipe of Object.values(HERB_CLEANING || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Alchemy (Cleaning)');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Alchemy');
    }

    // Alchemy - potions
    for (const recipe of Object.values(POTION_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Alchemy');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Alchemy');
        if (recipe.secondaryItem) addUsedIn(sources, recipe.secondaryItem, 'Alchemy');
    }

    // Woodworking - shafts from logs
    for (const recipe of Object.values(SHAFT_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Woodworking');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Woodworking');
    }

    // Woodworking - arrows from shafts + arrowheads
    for (const recipe of Object.values(ARROW_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Woodworking');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Woodworking');
        }
    }

    // Woodworking - bows from logs
    for (const recipe of Object.values(BOW_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Woodworking');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Woodworking');
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Woodworking');
        }
    }

    // Woodworking - staves from logs
    for (const recipe of Object.values(STAFF_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Woodworking');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Woodworking');
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Woodworking');
        }
    }

    // Crafting - jewelry from gems + bars
    for (const recipe of Object.values(JEWELRY || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Crafting');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Crafting');
        }
    }

    // Crafting - armor from hides
    for (const recipe of Object.values(ALL_ARMOR || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Crafting');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Crafting');
        }
    }

    // Crafting - magic armor from cloth
    for (const recipe of Object.values(ALL_MAGIC_ARMOR || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Crafting');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Crafting');
        }
    }

    // Glyphbinding - glyphs from ichor
    for (const recipe of Object.values(GLYPH_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Glyphbinding');
        }
        if (recipe.inputItem) addUsedIn(sources, recipe.inputItem, 'Glyphbinding');
    }

    // Glyphbinding - glyph cores from glyphs + residue
    for (const recipe of Object.values(GLYPH_CORE_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Glyphbinding');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Glyphbinding');
        }
    }

    // Glyphbinding - focus items from glyph cores
    for (const recipe of Object.values(FOCUS_RECIPES || {})) {
        for (const drop of recipe.drops || []) {
            addSource(sources, drop.itemId, 'Glyphbinding');
        }
        for (const input of recipe.inputItems || []) {
            addUsedIn(sources, input.itemId, 'Glyphbinding');
        }
    }

    // ============= COMBAT DROPS =============
    // Track which items have combat sources for spoiler logic
    const combatOnlyItems = new Set();

    for (const [zoneId, loot] of Object.entries(ZONE_LOOT || {})) {
        const zone = Object.values(COMBAT_ZONES).find(z => z.id === zoneId);
        const zoneName = zone ? zone.name : zoneId;

        const allDrops = [...(loot.common || []), ...(loot.rare || [])];
        for (const drop of allDrops) {
            if (sources.has(drop.itemId)) {
                const itemData = sources.get(drop.itemId);
                const isRare = (loot.rare || []).includes(drop);
                const combatSource = `Combat: ${zoneName}${isRare ? ' (rare)' : ''}`;
                if (!itemData.sources.includes(combatSource)) {
                    itemData.sources.push(combatSource);
                }
                // Track for spoiler logic - will check if combat-only later
                if (drop.itemId !== 'coins' && drop.itemId !== 'ashes') {
                    combatOnlyItems.add(drop.itemId);
                }
            }
        }
    }

    // ============= MOB-SPECIFIC DROPS =============
    // Boss weapons, set armor, pendants, and rings drop from individual mobs
    for (const [mobId, loot] of Object.entries(MOB_LOOT || {})) {
        // Find mob name from MOBS data
        const mobObj = Object.values(MOBS).find(m => m.id === mobId);
        const mobName = mobObj ? mobObj.name : mobId;

        const allDrops = [...(loot.common || []), ...(loot.rare || [])];
        for (const drop of allDrops) {
            if (sources.has(drop.itemId)) {
                const itemData = sources.get(drop.itemId);
                const isRare = (loot.rare || []).includes(drop);
                const mobSource = `Combat: ${mobName}${isRare ? ' (rare)' : ''}`;
                if (!itemData.sources.includes(mobSource)) {
                    itemData.sources.push(mobSource);
                }
                if (drop.itemId !== 'coins' && drop.itemId !== 'ashes') {
                    combatOnlyItems.add(drop.itemId);
                }
            }
        }
    }

    // ============= SHOP ITEMS =============
    for (const shopItem of getAllShopItems()) {
        // For quest-locked shop items, show as "Shop (Quest unlock)"
        if (shopItem.questUnlock) {
            addSource(sources, shopItem.id, 'Shop (Quest unlock)');
        } else {
            addSource(sources, shopItem.id, 'Shop');
        }
        // Daily free shop items grant a different item than their shop entry ID
        if (shopItem.grantItemId) {
            addSource(sources, shopItem.grantItemId, 'Shop (Daily Free)');
        }
    }

    // ============= QUEST REWARDS AND UNLOCKS =============
    // Build mapping of unlock IDs to item IDs
    const unlockToItemMap = {
        'glyph_pouch': 'glyph_pouch',
        'quiver_leather': 'leather_quiver',
        'quiver_ironwood': 'ironwood_quiver',
        'quiver_ashen': 'ashen_quiver',
        'quiver_veilstalker': 'veilstalker_quiver'
    };

    for (const [questNum, quest] of Object.entries(QUESTS || {})) {
        const questName = `Quest #${questNum}`;

        // Direct item rewards
        if (quest.rewards?.items) {
            for (const itemId of Object.keys(quest.rewards.items)) {
                addSource(sources, itemId, `${questName} (Reward)`);
            }
        }

        // Unlock rewards - map to actual items
        if (quest.rewards?.unlocks) {
            for (const unlockId of quest.rewards.unlocks) {
                const itemId = unlockToItemMap[unlockId];
                if (itemId) {
                    addSource(sources, itemId, `${questName} (Unlock)`);
                }
            }
        }
    }

    // Gold comes from multiple sources
    addSource(sources, 'coins', 'Combat');
    addSource(sources, 'coins', 'Pilfering');
    addSource(sources, 'coins', 'Selling Items');

    // ============= TALENT PROC DROPS (not spoilers - talents are visible) =============

    // Mining Gem Finder talent - proc can drop gems
    const gemItems = ['sapphire', 'emerald', 'ruby', 'diamond', 'astryx_stone', 'onyx'];
    for (const gemId of gemItems) {
        addSource(sources, gemId, 'Mining (Gem Finder talent)');
    }

    // Woodcutting Deep Roots talent - tree seeds and ancient seed
    const treeSeedItems = ['ironwood_seed', 'softbark_seed', 'goldleaf_seed', 'redgrain_seed', 'gravewood_seed', 'arcanewood_seed'];
    for (const seedId of treeSeedItems) {
        addSource(sources, seedId, 'Woodcutting (Deep Roots talent)');
    }
    addSource(sources, 'ancient_seed', 'Woodcutting (Deep Roots talent)');

    // Fishing Bycatch talent - seeds, herbs, bones
    const bycatchItems = [
        // Common tier
        'potato_seed', 'onion_seed', 'cabbage_seed', 'greenleaf_seed', 'duskleaf_seed',
        'grimy_greenleaf', 'grimy_duskleaf', 'small_bone',
        // Uncommon tier
        'tomato_seed', 'corn_seed', 'ironbloom_seed', 'emberroot_seed', 'sanctum_leaf_seed',
        'grimy_ironbloom', 'grimy_emberroot', 'grimy_sanctum_leaf', 'charred_bone',
        // Rare tier
        'sunpetal_seed', 'deepvine_seed', 'goldleaf_seed', 'redgrain_seed',
        'grimy_sunpetal', 'grimy_deepvine', 'grimy_stonebark', 'dragon_bone'
    ];
    for (const itemId of bycatchItems) {
        addSource(sources, itemId, 'Fishing (Bycatch talent)');
    }

    // ============= PET CONTRACT REWARDS =============
    for (const contract of Object.values(PET_CONTRACTS || {})) {
        const contractName = contract.name || contract.id;
        for (const reward of contract.rewards || []) {
            addSource(sources, reward.itemId, `Pet Contract: ${contractName}`);
        }
        // Insight drops from pet contracts (insightTiers field)
        if (contract.insightTiers) {
            for (const [petId, insightIds] of Object.entries(contract.insightTiers)) {
                const chance = contract.insightChance?.[petId] || 0;
                if (chance > 0) {
                    for (const insightId of insightIds) {
                        addSource(sources, insightId, `Pet Contract: ${contractName}`);
                    }
                }
            }
        }
    }

    // ============= PILFERING GUILD DOMAINS =============
    for (const domain of Object.values(GUILD_DOMAINS || {})) {
        const domainName = domain.name || domain.id;
        for (const itemId of domain.items || []) {
            addSource(sources, itemId, `Pilfering Guild (${domainName})`);
        }
    }

    // Pilfering - tier upgrade items (Pilferer's Network I/II/III)
    for (const [domainId, tierUpgrades] of Object.entries(DOMAIN_TIER_UPGRADES || {})) {
        const domain = Object.values(GUILD_DOMAINS).find(d => d.id === domainId);
        const domainName = domain?.name || domainId;
        for (const tierLevel of [1, 2, 3]) {
            for (const itemId of tierUpgrades[tierLevel] || []) {
                addSource(sources, itemId, `Pilfering Guild (${domainName})`);
            }
        }
    }

    // ============= AWAKENING TRIALS (rare source) =============
    // These items can drop from pet awakening trials
    const awakeningTrialItems = [
        // Common tier
        'common_timber', 'ironwood_logs', 'softbark_logs', 'raw_sand_smelt', 'raw_silver_sprat',
        'raw_blueback', 'small_bone', 'charred_bone', 'potato_seed', 'onion_seed', 'cabbage_seed',
        'greenleaf_seed', 'duskleaf_seed', 'grimy_greenleaf', 'grimy_duskleaf',
        // Rare tier
        'arcanewood_logs', 'ancient_seed', 'grimy_voidbloom', 'grimy_elderbark', 'gravewood_seed',
        'dragon_bone', 'void_bone', 'elder_bone'
    ];
    for (const itemId of awakeningTrialItems) {
        addSource(sources, itemId, 'Pet Awakening Trials');
    }

    // ============= MARK SPOILERS =============
    // Only items that EXCLUSIVELY come from combat are spoilers
    // If an item has a non-combat source (skill, shop, etc), it's not a spoiler
    for (const itemId of combatOnlyItems) {
        const itemData = sources.get(itemId);
        if (!itemData) continue;

        // Check if ALL sources are combat-related
        const hasNonCombatSource = itemData.sources.some(src => !src.startsWith('Combat:'));
        if (!hasNonCombatSource) {
            itemData.isSpoiler = true;
        }
    }

    return sources;
}

// Build sources on module load
const itemSources = buildItemSources();

/**
 * Get filtered items based on search and category
 */
function getFilteredItems() {
    let items = getAllItems();

    // Filter by category
    if (selectedCategory !== 'all') {
        items = items.filter(item => item.category === selectedCategory);
    }

    // Filter by search
    if (searchFilter.trim()) {
        const query = searchFilter.toLowerCase();
        items = items.filter(item => {
            const nameMatch = item.name.toLowerCase().includes(query);
            const sourceData = itemSources.get(item.id);
            const sourceMatch = sourceData?.sources.some(s => s.toLowerCase().includes(query));
            return nameMatch || sourceMatch;
        });
    }

    // Sort alphabetically
    items.sort((a, b) => a.name.localeCompare(b.name));

    return items;
}

/**
 * Format sources for display - truncate if too many
 */
function formatSourcesDisplay(sources) {
    const MAX_VISIBLE = 2;

    if (sources.length === 0) {
        return { text: 'Not obtainable', hasMore: false, allSources: [] };
    }

    if (sources.length <= MAX_VISIBLE) {
        return { text: sources.join(', '), hasMore: false, allSources: sources };
    }

    const visible = sources.slice(0, MAX_VISIBLE);
    const remaining = sources.length - MAX_VISIBLE;
    return {
        text: `${visible.join(', ')} +${remaining} more`,
        hasMore: true,
        allSources: sources
    };
}

/**
 * Format sources for tooltip - group by type
 */
function formatSourcesTooltip(sources) {
    const combatSources = sources.filter(s => s.startsWith('Combat:'));
    const skillSources = sources.filter(s => !s.startsWith('Combat:'));

    let html = '';

    if (skillSources.length > 0) {
        html += `<div class="tooltip-section"><div class="tooltip-section-title">Skills</div>`;
        html += skillSources.map(s => `<div class="tooltip-source">${s}</div>`).join('');
        html += '</div>';
    }

    if (combatSources.length > 0) {
        html += `<div class="tooltip-section"><div class="tooltip-section-title">Combat Drops</div>`;
        // Clean up combat source names
        const cleanedCombat = combatSources.map(s => s.replace('Combat: ', ''));
        html += cleanedCombat.map(s => `<div class="tooltip-source">${s}</div>`).join('');
        html += '</div>';
    }

    return html;
}

/**
 * Render a single item entry
 */
function renderItemEntry(item) {
    const sourceData = itemSources.get(item.id) || { sources: [], usedIn: [], isSpoiler: false };
    const isSpoiler = sourceData.isSpoiler && !showSpoilers && !revealedSpoilers.has(item.id);
    const spoilerClass = isSpoiler ? 'item-entry-spoiler' : '';
    const revealedClass = revealedSpoilers.has(item.id) ? 'revealed' : '';
    const onClick = isSpoiler ? `onclick="window.itemDatabaseModal.revealSpoiler('${item.id}')"` : '';

    const icon = getItemIcon(item.id);

    // Determine source text with truncation
    let sourcesText;
    let tooltipHtml = '';
    let hasTooltip = false;

    if (sourceData.sources.length > 0) {
        const formatted = formatSourcesDisplay(sourceData.sources);
        sourcesText = formatted.text;
        hasTooltip = formatted.hasMore;
        if (hasTooltip) {
            tooltipHtml = formatSourcesTooltip(formatted.allSources);
        }
    } else if (item.type === 'currency') {
        sourcesText = 'Various';
    } else {
        sourcesText = 'Not obtainable';
    }

    // Build usedIn text if present
    let usedInHtml = '';
    if (sourceData.usedIn.length > 0) {
        usedInHtml = `
            <div class="item-entry-usedin">
                <span class="usedin-label">Used in:</span> ${sourceData.usedIn.join(', ')}
            </div>
        `;
    }

    // Sources with optional tooltip
    const sourcesClass = hasTooltip ? 'has-tooltip' : '';
    const tooltipAttr = hasTooltip ? `data-tooltip-content="${tooltipHtml.replace(/"/g, '&quot;')}"` : '';

    return `
        <div class="item-entry ${spoilerClass} ${revealedClass}" data-item-id="${item.id}" ${onClick}>
            <div class="item-entry-icon" style="background-color: ${item.color || '#666'}">
                ${icon}
            </div>
            <div class="item-entry-info">
                <div class="item-entry-name">
                    ${item.name}
                    ${sourceData.isSpoiler ? '<span class="item-spoiler-badge">🔒</span>' : ''}
                </div>
                <div class="item-entry-sources ${sourcesClass}" ${tooltipAttr}>
                    <span class="source-label">Source:</span> ${sourcesText}
                    ${hasTooltip ? '<span class="sources-expand-hint">ⓘ</span>' : ''}
                </div>
                ${usedInHtml}
            </div>
        </div>
    `;
}

/**
 * Render category tabs
 */
function renderCategoryTabs() {
    const categories = ['all', ...new Set(getAllItems().map(i => i.category))].filter(c => CATEGORY_CONFIG[c]);

    return categories.map(cat => {
        const config = CATEGORY_CONFIG[cat] || { name: cat, icon: '📦' };
        const activeClass = selectedCategory === cat ? 'active' : '';
        const count = cat === 'all'
            ? getAllItems().length
            : getAllItems().filter(i => i.category === cat).length;

        return `
            <button class="item-db-tab ${activeClass}" onclick="window.itemDatabaseModal.setCategory('${cat}')">
                <span class="tab-icon">${config.icon}</span>
                <span class="tab-name">${config.name}</span>
                <span class="tab-count">${count}</span>
            </button>
        `;
    }).join('');
}

/**
 * Render body content
 */
function renderBodyContent() {
    const items = getFilteredItems();

    if (items.length === 0) {
        return '<div class="item-db-no-results">No items found matching your search.</div>';
    }

    return `
        <div class="item-db-grid">
            ${items.map(item => renderItemEntry(item)).join('')}
        </div>
    `;
}

/**
 * Render the full modal content
 */
function renderModalContent() {
    const spoilerToggleClass = showSpoilers ? 'active' : '';
    const spoilerToggleIcon = showSpoilers ? '👁️' : '🔒';
    const spoilerToggleText = showSpoilers ? 'Hide Spoilers' : 'Show Spoilers';
    const spoilersVisibleClass = showSpoilers ? 'item-db-spoilers-visible' : '';
    const totalItems = getAllItems().length;

    return `
        <div class="modal-overlay" onclick="window.itemDatabaseModal.handleOverlayClick(event)">
            <div class="modal-content item-database-modal ${spoilersVisibleClass}">
                <div class="modal-header">
                    <h2>📦 Item Database</h2>
                    <div class="item-db-header-controls">
                        <span class="item-db-count">${totalItems} items</span>
                        <button class="spoiler-toggle ${spoilerToggleClass}" onclick="window.itemDatabaseModal.toggleSpoilers()">
                            <span class="spoiler-toggle-icon">${spoilerToggleIcon}</span>
                            <span>${spoilerToggleText}</span>
                        </button>
                        <button class="modal-close" onclick="window.itemDatabaseModal.close()">&times;</button>
                    </div>
                </div>
                <div class="item-db-search-bar">
                    <input type="text"
                           class="item-db-search"
                           placeholder="Search items or sources..."
                           value="${searchFilter}"
                           oninput="window.itemDatabaseModal.handleSearch(this.value)">
                    ${searchFilter ? '<button class="item-db-search-clear" onclick="window.itemDatabaseModal.clearSearch()">×</button>' : ''}
                </div>
                <div class="item-db-tabs-container">
                    <div class="item-db-tabs">
                        ${renderCategoryTabs()}
                    </div>
                </div>
                <div class="modal-body">
                    <div class="item-db-content">
                        ${renderBodyContent()}
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Create and show tooltip for sources
 */
let activeTooltip = null;

function showSourcesTooltip(element) {
    const content = element.dataset.tooltipContent;
    if (!content) return;

    // Remove existing tooltip
    hideSourcesTooltip();

    const tooltip = document.createElement('div');
    tooltip.className = 'item-sources-tooltip';
    tooltip.innerHTML = content;

    document.body.appendChild(tooltip);
    activeTooltip = tooltip;

    // Position tooltip
    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left = rect.left;
    let top = rect.bottom + 8;

    // Keep within viewport
    if (left + tooltipRect.width > window.innerWidth - 20) {
        left = window.innerWidth - tooltipRect.width - 20;
    }
    if (top + tooltipRect.height > window.innerHeight - 20) {
        top = rect.top - tooltipRect.height - 8;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
}

function hideSourcesTooltip() {
    if (activeTooltip) {
        activeTooltip.remove();
        activeTooltip = null;
    }
}

/**
 * Setup tooltip listeners for the modal
 */
function setupTooltipListeners() {
    if (!modalElement) return;

    modalElement.addEventListener('mouseenter', (e) => {
        const target = e.target.closest('.item-entry-sources.has-tooltip');
        if (target) {
            showSourcesTooltip(target);
        }
    }, true);

    modalElement.addEventListener('mouseleave', (e) => {
        const target = e.target.closest('.item-entry-sources.has-tooltip');
        if (target) {
            hideSourcesTooltip();
        }
    }, true);
}

/**
 * Open the Item Database modal
 */
export function openItemDatabaseModal() {
    searchFilter = '';
    selectedCategory = 'all';
    revealedSpoilers.clear(); // Reset individually revealed spoilers

    modalElement = document.createElement('div');
    modalElement.id = 'item-database-modal-container';
    modalElement.innerHTML = renderModalContent();
    document.body.appendChild(modalElement);

    // Focus search input
    const searchInput = modalElement.querySelector('.item-db-search');
    if (searchInput) {
        searchInput.focus();
    }

    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown);

    // Setup tooltip listeners
    setupTooltipListeners();
}

/**
 * Close the Item Database modal
 */
export function closeItemDatabaseModal() {
    hideSourcesTooltip();
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
    }
    document.removeEventListener('keydown', handleKeyDown);
}

/**
 * Update only the content area
 */
function updateContent() {
    if (!modalElement) return;

    const contentArea = modalElement.querySelector('.item-db-content');
    if (contentArea) {
        contentArea.innerHTML = renderBodyContent();
    }
}

/**
 * Full modal update
 */
function updateModal() {
    if (!modalElement) return;

    const scrollTop = modalElement.querySelector('.modal-body')?.scrollTop || 0;
    modalElement.innerHTML = renderModalContent();

    const modalBody = modalElement.querySelector('.modal-body');
    if (modalBody) {
        modalBody.scrollTop = scrollTop;
    }

    const searchInput = modalElement.querySelector('.item-db-search');
    if (searchInput && searchFilter.length > 0) {
        searchInput.focus();
        searchInput.setSelectionRange(searchFilter.length, searchFilter.length);
    }
}

/**
 * Handle search input
 */
function handleSearch(value) {
    searchFilter = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        updateContent();
    }, 100);
}

/**
 * Clear search
 */
function clearSearch() {
    searchFilter = '';
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    updateModal();
    const searchInput = modalElement?.querySelector('.item-db-search');
    if (searchInput) {
        searchInput.focus();
    }
}

/**
 * Set category filter
 */
function setCategory(category) {
    selectedCategory = category;

    if (!modalElement) return;

    // Update tab active states without full modal rebuild
    const tabs = modalElement.querySelectorAll('.item-db-tab');
    tabs.forEach(tab => {
        const tabCat = tab.getAttribute('onclick')?.match(/'([^']+)'/)?.[1];
        tab.classList.toggle('active', tabCat === category);
    });

    // Update content area only
    updateContent();
}

/**
 * Toggle all spoilers
 */
function toggleSpoilers() {
    showSpoilers = !showSpoilers;
    updateModal();
}

/**
 * Reveal a single spoiler item
 */
function revealSpoiler(itemId) {
    revealedSpoilers.add(itemId);
    updateContent();
}

/**
 * Handle overlay click
 */
function handleOverlayClick(event) {
    if (event.target.classList.contains('modal-overlay')) {
        closeItemDatabaseModal();
    }
}

/**
 * Handle keyboard events
 */
function handleKeyDown(event) {
    if (event.key === 'Escape') {
        closeItemDatabaseModal();
    }
}

// Expose to window for onclick handlers
window.itemDatabaseModal = {
    open: openItemDatabaseModal,
    close: closeItemDatabaseModal,
    handleSearch,
    clearSearch,
    setCategory,
    toggleSpoilers,
    revealSpoiler,
    handleOverlayClick
};

export default {
    openItemDatabaseModal,
    closeItemDatabaseModal
};
